// The Workshop's hero: the Jack-o'-Lantern generator, live. It runs the pumpkin's own code in the sandboxed preview
// frame (preview-frame.ts; three.js never loads in the page) on the lamplit "workshop" stage, with its candle and bloom,
// and steps its face through the looks (Classic, Scary, Goofy) until someone picks one. The `generator-hero` section's
// chips drive it; on phones, low-power machines and under reduced motion the poster stays until a chip or "Turn it" is
// pressed. Only the Jack-o'-Lantern prop is lit here: the free Pumpkin Head's candle is a quest reward and never shows.

import { h } from "../../dom.ts";
import { presetsFor } from "../../pages/store.ts";
import { PACKAGES, startAssetPreview } from "../../preview-frame.ts";
import { controlsFor, type Manifest, type Value } from "../../store.ts";

export const GENERATOR_ID = "threedom/halloween-pumpkin";
export const GENERATOR_BASE = `${PACKAGES}${GENERATOR_ID}/`;
export const GENERATOR_HREF = `/store/a/${GENERATOR_ID}`;
/** How long each look stays while the hero cycles. */
export const CYCLE_MS = 3400;

/** The looks the chips offer, in order. */
export const LOOKS = ["Classic", "Scary", "Goofy"] as const;
export type Look = (typeof LOOKS)[number];

/**
 * The params for a look: the asset's defaults (the candle `lit` included, so the stage's candle light comes on) with
 * the look's face settings on top. Unknown looks give the defaults.
 */
export function lookParams(m: Pick<Manifest, "params">, look: string): Record<string, Value> {
  const out: Record<string, Value> = {};
  for (const c of controlsFor(m, {})) out[c.key] = c.value;
  const p = presetsFor(m).find(([n]) => n === look)?.[1] ?? {};
  return { ...out, ...p };
}

/** The next look in the cycle. */
export const nextLook = (l: Look): Look => LOOKS[(LOOKS.indexOf(l) + 1) % LOOKS.length]!;

type State = "poster" | "loading" | "live" | "failed";

/** The one generator on the page: the hero mounts it, the chips steer it. */
class Generator {
  look: Look = "Classic";
  state: State = "poster";
  private show: ((p: Record<string, Value>) => void) | null = null;
  private manifest: Manifest | null = null;
  private timer = 0;
  private picked = false;
  private listeners = new Set<() => void>();

  /** Subscribe to look and state changes; unsubscribes itself once `el` leaves the page. */
  watch(el: HTMLElement, f: () => void): void {
    let seen = false;
    const run = () => {
      if (el.isConnected) seen = true;
      else if (seen) return void this.listeners.delete(run);
      f();
    };
    this.listeners.add(run);
    f();
  }

  private emit(): void {
    for (const f of [...this.listeners]) f();
  }

  /** Mount the live generator into `host`; resolves after its first frame, rejects if it can't run. */
  mount(host: HTMLElement, cycle: boolean): Promise<{ dispose(): void }> {
    this.reset();
    this.state = "loading";
    this.emit();
    return new Promise((resolve, reject) => {
      fetch(`${GENERATOR_BASE}asset.json`)
        .then((r) => (r.ok ? (r.json() as Promise<Manifest>) : Promise.reject(new Error(`asset.json ${r.status}`))))
        .then((m) => {
          this.manifest = m;
          const p = startAssetPreview(host, {
            base: GENERATOR_BASE,
            manifest: m,
            mode: "live",
            stage: "workshop",
            decorative: true,
            onLoaded: () => {
              this.state = "live";
              this.emit();
              if (cycle && !this.picked) this.startCycle(host);
              resolve({ dispose: () => this.reset() });
            },
            onNote: (t) => {
              if (/unavailable/i.test(t) && this.state !== "live") {
                this.state = "failed";
                this.emit();
                reject(new Error(t));
              }
            },
          });
          this.show = p.show;
          p.show(lookParams(m, this.look));
        })
        .catch((e) => {
          this.state = "failed";
          this.emit();
          reject(e);
        });
    });
  }

  private startCycle(host: HTMLElement): void {
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => {
      if (!host.isConnected || this.picked) return window.clearInterval(this.timer);
      if (document.hidden) return;
      this.apply(nextLook(this.look));
    }, CYCLE_MS);
  }

  private apply(look: Look): void {
    this.look = look;
    if (this.show && this.manifest) this.show(lookParams(this.manifest, look));
    this.emit();
  }

  /** A chip: show that look and stop cycling. */
  pick(look: Look): void {
    this.picked = true;
    window.clearInterval(this.timer);
    this.apply(look);
  }

  get live(): boolean {
    return this.state === "live" || this.state === "loading";
  }

  reset(): void {
    window.clearInterval(this.timer);
    this.show = null;
    this.state = "poster";
    this.picked = false;
  }
}

export const generator = new Generator();

/** The zone's hero media box (the shell's `.zone-hero-media`), for mounting on demand. */
function heroMedia(from: HTMLElement): HTMLElement | null {
  return from.closest(".zone")?.querySelector<HTMLElement>(".zone-hero-media") ?? null;
}

/** Start the generator in the hero now (a chip or "Turn it" on a device that kept the poster). */
export function turnOn(from: HTMLElement): void {
  if (generator.live) return;
  const media = heroMedia(from);
  if (!media) return;
  media.querySelector(".zone-hero-3d")?.remove();
  const host = h("div", { class: "zone-hero-3d ws-hero-3d", "aria-hidden": "true" });
  media.append(host);
  generator.mount(host, false).then(
    () => host.classList.add("on"),
    () => host.remove(),
  );
}
